import { ModalProps } from '../../types/modal'
import Modal from '../shared/Modal'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import Button from '@components/shared/Button'
import { useRouter } from 'next/router'
import {
  DAOS_REFS,
  isUsersApiEnabled,
  REFERRER_KEY,
  USERS_API_URL,
} from 'utils/constants'
import { PartnerRef } from '@components/referrals/ReferrerDaoForm'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import ProfileImage from '@components/profile/ProfileImage'
import { ProfileDetails } from '../profile/useProfileDetails'
import { useTranslation } from 'next-i18next'
import { milliseconds } from 'date-fns'

const fetchPartnerProfileDetails = async (
  profileName: string,
): Promise<ProfileDetails | null> => {
  if (!isUsersApiEnabled()) return null
  try {
    const profileResp = await fetch(`${USERS_API_URL}/user/search`, {
      method: 'POST',
      body: JSON.stringify({ username: profileName }),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!profileResp.ok) {
      console.error(
        `Error fetching partner profile details: partner=${profileName}, status=${profileResp.status}`,
      )
      return null
    }

    const data = (await profileResp.json()) as {
      users: Record<string, ProfileDetails | undefined>
    }

    return data.users[profileName] || null
  } catch (e) {
    console.error(
      `Error fetching partner profile details: partner=${profileName}`,
      e,
    )
    return null
  }
}

const ConfirmPartnerModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation('partner-confirm')
  const [, setLocalReferrer] = useLocalStorage(REFERRER_KEY)
  const router = useRouter()
  const [partner] = useState(() =>
    router.query.partner ? String(router.query.partner) : undefined,
  )

  const daoPartner = useMemo(
    () =>
      (partner &&
        DAOS_REFS.find(
          (x) => x.ref?.toLowerCase() === partner.toLowerCase(),
        )) ||
      undefined,
    [partner],
  )

  const {
    data: partnerProfileDetails,
    isInitialLoading: loadingPartnerProfileDetails,
  } = useQuery({
    queryKey: ['partner-profile-details', partner],
    queryFn: () => {
      if (!partner) {
        throw new Error('Partner is required to fetch profile details')
      }
      return fetchPartnerProfileDetails(partner)
    },
    cacheTime: milliseconds({ minutes: 30 }),
    staleTime: milliseconds({ minutes: 30 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!partner && !daoPartner,
  })

  const referrerDetails = useMemo(() => {
    if (daoPartner) {
      return {
        name: daoPartner.name,
        ref: daoPartner.ref,
        wallet: daoPartner.wallet,
      } satisfies PartnerRef
    }
    if (!partnerProfileDetails?.wallet) {
      return null
    }
    return {
      name: partnerProfileDetails.username,
      ref: partnerProfileDetails.wallet,
      wallet: partnerProfileDetails.wallet,
    } satisfies PartnerRef
  }, [partnerProfileDetails, daoPartner])

  const reloadWithoutPartnerParam = useCallback(() => {
    const currentUrl = new URL(router.asPath, window.location.origin)
    const searchParams = currentUrl.searchParams
    searchParams.delete('partner')
    const searchParamsString = searchParams.toString()

    void router.replace(
      currentUrl.pathname +
        (searchParamsString ? '?' + searchParamsString : ''),
      undefined,
      { shallow: true },
    )
    onClose()
  }, [onClose, router])

  const handleAccept = useCallback(() => {
    if (!referrerDetails) {
      return
    }

    setLocalReferrer(referrerDetails)
    reloadWithoutPartnerParam()
  }, [referrerDetails, reloadWithoutPartnerParam, setLocalReferrer])

  return (
    <Modal isOpen={isOpen} onClose={onClose} disableOutsideClose hideClose>
      {!daoPartner &&
      !partnerProfileDetails?.wallet &&
      !loadingPartnerProfileDetails ? (
        <>
          <h2 className="mb-1 text-center">{t('not-found.title')}</h2>
          <p className="mb-4 text-center">
            {t('not-found.description', { partner: partner || 'unknown' })}
          </p>
          <div className="flex justify-center">
            <Button onClick={reloadWithoutPartnerParam}>
              {t('not-found.ignore')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <h2 className="mb-1 text-center">{t('accept.title')}</h2>
          <div className="flex justify-center py-4">
            <div className="flex flex-col items-center">
              <ProfileImage
                imageSize="64"
                placeholderSize="40"
                imageSource={{
                  type: 'profileWalletPk',
                  profileWalletPk: partnerProfileDetails?.hasImage
                    ? partnerProfileDetails?.wallet
                    : null,
                  fallbackStrategy: 'placeholder',
                }}
              />
              <p className="mt-1 text-base font-bold text-th-fgd-1">
                {partnerProfileDetails?.username || daoPartner?.name}
              </p>
            </div>
          </div>
          <p className="mb-1 text-center text-th-fgd-1">
            {t('accept.invited-subtitle')}
          </p>
          <p className="mb-4 text-center">{t('accept.description')}</p>
          <div className="flex justify-center">
            <Button onClick={handleAccept}>{t('accept.start')}</Button>
          </div>
        </>
      )}
    </Modal>
  )
}

export default ConfirmPartnerModal
